import type {
  AssetInfoItemsHistoryRequest,
  AssetInfoItemsHistoryResponse,
  InfoItemsHistoryDateRangesResponse,
  AssetOperationExecutionTarget,
  TagCategoriesSummaryResponse,
  ActivityHistoryDateRangesResponse,
  AssetsTotalCountResponse,
  ActivityHistoryResponse,
  ActivityHistoryRequest,
  AssetOperationExecuteResponse,
  AssetsBasicInformationResponse,
  AssetsBasicInformationRequest,
  AssetOperationAvailabilityItem,
  AccessGroup,
  AssetsIprIdsRequest,
  AssetsListSelectionRangeResponse,
  AssetOperationsStatusesRequest,
  AssetOperationsStatusesResponse,
  AssetTagsResponse,
  AssetTagSuggestionsResponse,
  AssetTagAddRequest,
  AssetTagBatchRequest,
  AssetTagBatchResponse,
  FilterOptionsResponse,
} from '@fleet-bffs/api-contract';
import { bffWebApi, TagTypes } from '~/api';
import type { ErrorType } from '~/data/ErrorRecord';
import logger from '~/utils/logger/logger';
import { setAssetsActions } from '~/domain/assets/store/hfs-actions/reducer';
import type { AccessGroupMachine } from '~/api/access-groups-api/types';
import type { GetAssetListResponse } from '~/api/machine-api';
import type { SendCommandResponse } from '~/api/robotic-mower-api';
import { collectFullyFailedInsertedTags } from './batch-tag-suggestions';
import { READ_IPR_IDS_PER_REQUEST, postInIprIdChunks } from './chunked-ipr-ids';

export type PatchMachinesTagsRelationshipResponse = {
  success: boolean;
};

/** Select all on the paged list: `requestedAt` gives every tick its own request. */
export interface PagedListSelectionRangeArgs {
  filters: Record<string, string | string[]>;
  requestedAt: number;
}

const selectionRangeUrl = (params: Record<string, string | string[]>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    (Array.isArray(value) ? value : [value]).forEach((item) => search.append(key, item));
  });
  return `${process.env.ENDPOINT_BFF}/assets/list/selection-range?${search.toString()}`;
};

const mergeTagBatchResponses = (results: AssetTagBatchResponse[]): AssetTagBatchResponse => ({
  results: results.flatMap((result) => result.results),
});

const TAG_BATCH_NOT_APPLIED = 'not_applied';

const failedTagBatch =
  (tags: string[]) =>
  (iprIds: string[]): AssetTagBatchResponse => ({
    results: iprIds.map((iprId) => ({
      iprId,
      assignedTags: [],
      failedTags: tags.map((tag) => ({ tag, reason: TAG_BATCH_NOT_APPLIED })),
    })),
  });

const assetsAPI = bffWebApi.injectEndpoints({
  endpoints: (build) => {
    return {
      tagsAssetsCounts: build.query<TagCategoriesSummaryResponse, void>({
        query: () => ({
          url: `${process.env.ENDPOINT_BFF}/assets/tags/counts`,
        }),
      }),
      assetsTypesCounts: build.query<AssetsTotalCountResponse, void>({
        query: () => ({
          url: `${process.env.ENDPOINT_BFF}/assets/total-count`,
        }),
      }),
      getFilterOptions: build.query<FilterOptionsResponse, Record<string, string> | undefined>({
        query: (params) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/filter-options?${new URLSearchParams(
            params || {}
          ).toString()}`,
        }),
        providesTags: [TagTypes.FilterOptions, TagTypes.FilterOptionsTags],
        keepUnusedDataFor: 30,
      }),
      getAssetsBasicInformation: build.query<
        AssetsBasicInformationResponse,
        AssetsBasicInformationRequest
      >({
        queryFn: ({ iprIds, attributes }, _api, _extraOptions, baseQuery) =>
          postInIprIdChunks<AssetsBasicInformationResponse, ErrorType>(
            baseQuery,
            `${process.env.ENDPOINT_BFF}/assets/basic-information`,
            iprIds,
            (chunk) => ({ iprIds: chunk, attributes }),
            (results) => ({ items: results.flatMap((result) => result.items) }),
            { chunkSize: READ_IPR_IDS_PER_REQUEST }
          ),
      }),
      getAssetAccessGroups: build.query<AccessGroup[], string>({
        query: (iprId) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/access-groups/${iprId}`,
        }),
      }),
      getAssetsForAccessGroups: build.query<AccessGroupMachine[], { search: string }>({
        query: ({ search }) => ({
          url: `/machines/v3?filter[free_text]=${search}`,
          method: 'GET',
        }),
        providesTags: [TagTypes.AccessGroupAssets],
        keepUnusedDataFor: 0,
        transformResponse(data: GetAssetListResponse) {
          return data.machines.map((machine) => ({
            id: machine.assetDetails.id,
            name: machine.assetDetails.name,
            model: machine.assetDetails.model,
            smallImage: machine.assetDetails.images.smallImage,
            otherAccessGroups: [],
          }));
        },
      }),

      patchMachinesTagsRelationship: build.mutation<
        PatchMachinesTagsRelationshipResponse,
        {
          assetId: string;
          tagsToBeAdded: string[];
          tagsToBeRemoved: string[];
        }
      >({
        query: ({ assetId, tagsToBeAdded, tagsToBeRemoved }) => ({
          url: `/machines/${assetId}/relationship/tags`,
          method: 'PATCH',
          body: {
            tagsToBeAdded,
            tagsToBeRemoved,
          },
        }),
      }),
      toggleMissionStatus: build.mutation<
        SendCommandResponse,
        {
          machineIds: AssetId[];
          enabled: boolean;
          missionId: number;
        }
      >({
        query: (body) => ({
          url: '/machines/robotic/update-mission',
          method: 'POST',
          body,
        }),
      }),
      executeHfsAction: build.mutation<
        AssetOperationExecuteResponse,
        AssetOperationExecutionTarget
      >({
        query: (body: AssetOperationExecutionTarget) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/operation/execute`,
          method: 'POST',
          body,
        }),
      }),
      executeHfsActions: build.mutation<
        AssetOperationExecuteResponse[],
        AssetOperationExecutionTarget[]
      >({
        query: (body: AssetOperationExecutionTarget[]) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/operations/execute`,
          method: 'POST',
          body,
        }),
      }),
      getHfsOperationsStatuses: build.query<
        AssetOperationsStatusesResponse,
        AssetOperationsStatusesRequest
      >({
        query: (body) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/operations/statuses`,
          method: 'POST',
          body,
        }),
      }),
      getInfoItemsHistory: build.mutation<
        AssetInfoItemsHistoryResponse,
        AssetInfoItemsHistoryRequest
      >({
        query: (body: AssetInfoItemsHistoryRequest) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/history/info-items`,
          method: 'POST',
          body,
        }),
      }),
      getLatestInfoItemTerminationDate: build.query<
        InfoItemsHistoryDateRangesResponse,
        { diamGroupId: string; iprId: string }
      >({
        query: ({ diamGroupId, iprId }) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/history/info-items/latest-termination-date/${diamGroupId}/${iprId}`,
        }),
      }),
      getActivityHistory: build.mutation<ActivityHistoryResponse, ActivityHistoryRequest>({
        query: (body: AssetInfoItemsHistoryRequest) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/history/activity`,
          method: 'POST',
          body,
        }),
      }),
      getAvailableActions: build.query<AssetOperationAvailabilityItem[], AssetsIprIdsRequest>({
        query: (body) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/operation/availability`,
          method: 'POST',
          body,
        }),
        async onQueryStarted(body, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              setAssetsActions({
                actions: data,
                iprIds: body.iprIds,
              })
            );
          } catch (error) {
            logger.error('Failed to fetch assets operations availability', error);
          }
        },
      }),
      getActivityHistoryEarliestAvailableDate: build.query<
        ActivityHistoryDateRangesResponse,
        { diamGroupId: string; iprId: string }
      >({
        query: ({ diamGroupId, iprId }) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/history/activity/earliest-available-date/${diamGroupId}/${iprId}`,
        }),
      }),
      getListSelectionRange: build.query<
        AssetsListSelectionRangeResponse,
        Record<string, string | string[]>
      >({
        query: (params) => ({ url: selectionRangeUrl(params) }),
      }),
      getPagedListSelectionRange: build.query<
        AssetsListSelectionRangeResponse,
        PagedListSelectionRangeArgs
      >({
        query: ({ filters }) => ({ url: selectionRangeUrl(filters) }),
        keepUnusedDataFor: 0,
      }),
      getTagSuggestions: build.query<AssetTagSuggestionsResponse, void>({
        query: () => ({
          url: `${process.env.ENDPOINT_BFF}/assets/tags/suggestions`,
        }),
        providesTags: [TagTypes.AssetTagSuggestions],
      }),
      getAssetTags: build.query<AssetTagsResponse, string>({
        query: (iprId) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/${iprId}/tags`,
        }),
        providesTags: (_result, _error, iprId) => [{ type: TagTypes.AssetTags, id: iprId }],
      }),
      getAssetTagsByIds: build.query<{ tagsByIprId: Record<string, string[]> }, string[]>({
        queryFn: (iprIds, _api, _extraOptions, baseQuery) =>
          postInIprIdChunks<{ tagsByIprId: Record<string, string[]> }, ErrorType>(
            baseQuery,
            `${process.env.ENDPOINT_BFF}/assets/tags/by-ids`,
            iprIds,
            (chunk) => ({ iprIds: chunk }),
            (results) => ({
              tagsByIprId: Object.assign({}, ...results.map((result) => result.tagsByIprId)),
            }),
            { chunkSize: READ_IPR_IDS_PER_REQUEST }
          ),
        providesTags: (_result, _error, iprIds) =>
          iprIds.map((id) => ({ type: TagTypes.AssetTags, id })),
      }),
      addAssetTag: build.mutation<void, AssetTagAddRequest & { iprId: string }>({
        query: ({ iprId, tag }) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/${iprId}/tags`,
          method: 'POST',
          body: { tag },
        }),
        invalidatesTags: (_result, _error, { iprId }) => [
          { type: TagTypes.AssetTags, id: iprId },
          TagTypes.FilterOptionsTags,
        ],
        async onQueryStarted({ iprId, tag }, { dispatch, queryFulfilled }) {
          const tagsPatch = dispatch(
            assetsAPI.util.updateQueryData('getAssetTags', iprId, (draft) => {
              if (!draft.includes(tag)) {
                draft.push(tag);
              }
            })
          );
          const suggestionsPatch = dispatch(
            assetsAPI.util.updateQueryData('getTagSuggestions', undefined, (draft) => {
              if (!draft.includes(tag)) {
                draft.push(tag);
              }
            })
          );
          try {
            await queryFulfilled;
          } catch {
            tagsPatch.undo();
            suggestionsPatch.undo();
          }
        },
      }),
      removeAssetTag: build.mutation<void, { iprId: string; tag: string }>({
        query: ({ iprId, tag }) => ({
          url: `${process.env.ENDPOINT_BFF}/assets/${iprId}/tags/remove`,
          method: 'POST',
          body: { tag },
        }),
        invalidatesTags: (_result, _error, { iprId }) => [
          { type: TagTypes.AssetTags, id: iprId },
          TagTypes.AssetTagSuggestions,
          TagTypes.FilterOptionsTags,
        ],
        async onQueryStarted({ iprId, tag }, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            assetsAPI.util.updateQueryData('getAssetTags', iprId, (draft) => {
              const idx = draft.indexOf(tag);
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patch.undo();
          }
        },
      }),
      batchAssignTags: build.mutation<AssetTagBatchResponse, AssetTagBatchRequest>({
        queryFn: ({ tags, iprIds }, _api, _extraOptions, baseQuery) =>
          postInIprIdChunks<AssetTagBatchResponse, ErrorType>(
            baseQuery,
            `${process.env.ENDPOINT_BFF}/assets/tags/batch-assign`,
            iprIds,
            (chunk) => ({ tags, iprIds: chunk }),
            mergeTagBatchResponses,
            { failedIprIds: failedTagBatch(tags) }
          ),
        invalidatesTags: (_result, _error, { iprIds }) => [
          ...iprIds.map((id) => ({ type: TagTypes.AssetTags, id })),
          TagTypes.FilterOptionsTags,
        ],
        async onQueryStarted({ tags, iprIds }, { dispatch, queryFulfilled }) {
          const insertedSuggestions = new Set<string>();
          const suggestionsPatch = dispatch(
            assetsAPI.util.updateQueryData('getTagSuggestions', undefined, (draft) => {
              for (const tag of tags) {
                if (!draft.includes(tag)) {
                  draft.push(tag);
                  insertedSuggestions.add(tag);
                }
              }
            })
          );
          try {
            const { data } = await queryFulfilled;
            const tagsToStrip = collectFullyFailedInsertedTags(
              data,
              insertedSuggestions,
              iprIds.length
            );
            if (tagsToStrip.size > 0) {
              dispatch(
                assetsAPI.util.updateQueryData('getTagSuggestions', undefined, (draft) =>
                  draft.filter((tag) => !tagsToStrip.has(tag))
                )
              );
            }
          } catch {
            suggestionsPatch.undo();
          }
        },
      }),
      batchUnassignTags: build.mutation<AssetTagBatchResponse, AssetTagBatchRequest>({
        queryFn: ({ tags, iprIds }, _api, _extraOptions, baseQuery) =>
          postInIprIdChunks<AssetTagBatchResponse, ErrorType>(
            baseQuery,
            `${process.env.ENDPOINT_BFF}/assets/tags/batch-unassign`,
            iprIds,
            (chunk) => ({ tags, iprIds: chunk }),
            mergeTagBatchResponses,
            { failedIprIds: failedTagBatch(tags) }
          ),
        invalidatesTags: (_result, _error, { iprIds }) => [
          ...iprIds.map((id) => ({ type: TagTypes.AssetTags, id })),
          TagTypes.AssetTagSuggestions,
          TagTypes.FilterOptionsTags,
        ],
      }),
    };
  },
});

export const {
  useGetLatestInfoItemTerminationDateQuery,
  usePatchMachinesTagsRelationshipMutation,
  useToggleMissionStatusMutation,
  useExecuteHfsActionMutation,
  useExecuteHfsActionsMutation,
  useTagsAssetsCountsQuery,
  useGetAvailableActionsQuery,
  useGetInfoItemsHistoryMutation,
  useGetActivityHistoryMutation,
  useGetActivityHistoryEarliestAvailableDateQuery,
  useGetFilterOptionsQuery,
  useLazyAssetsTypesCountsQuery,
  useGetListSelectionRangeQuery,
  useGetPagedListSelectionRangeQuery,
  useGetAssetAccessGroupsQuery,
  useGetHfsOperationsStatusesQuery,
  useGetTagSuggestionsQuery,
  useGetAssetTagsQuery,
  useGetAssetTagsByIdsQuery,
  useAddAssetTagMutation,
  useRemoveAssetTagMutation,
  useBatchAssignTagsMutation,
  useBatchUnassignTagsMutation,
  useGetAssetsBasicInformationQuery,
} = assetsAPI;

export default assetsAPI;
